/**
 * Reducer for the voicemail feature.
 */
import ReducerRegistry from '../base/redux/ReducerRegistry';
import { VOICEMAIL_INBOX_LOADED, VOICEMAIL_INBOX_LOADING, VOICEMAIL_MESSAGE_SELECTED, VOICEMAIL_MESSAGE_DELETED, VOICEMAIL_MESSAGE_SEEN, VOICEMAIL_UNREAD_COUNT_UPDATED, VOICEMAIL_CAPTURE_REQUESTED, VOICEMAIL_RECORDING_STARTED, VOICEMAIL_RECORDING_COMPLETE, VOICEMAIL_RECORDING_CANCELLED, VOICEMAIL_RECORDING_SENT, VOICEMAIL_PROMPT_SHOWN, VOICEMAIL_PROMPT_DISMISSED, VOICEMAIL_PLAYER_OPENED, VOICEMAIL_PLAYER_CLOSED, VOICEMAIL_ERROR, VOICEMAIL_CLEAR_ERROR } from './actionTypes';
const INITIAL_STATE = {
    messages: [],
    totalCount: 0,
    unreadCount: 0,
    isLoading: false,
    inboxOpen: false,
    currentMessage: null,
    playbackUrl: null,
    isPlayerOpen: false,
    isCaptureRequested: false,
    isRecording: false,
    isRecordingReadyToSend: false,
    recordingSession: null,
    isPromptVisible: false,
    promptData: null,
    error: null
};
ReducerRegistry.register('features/voicemail', (state = INITIAL_STATE, action) => {
    switch (action.type) {
        case VOICEMAIL_INBOX_LOADING:
            return { ...state, isLoading: true };
        case VOICEMAIL_INBOX_LOADED:
            return {
                ...state,
                messages: action.messages || [],
                totalCount: action.totalCount || 0,
                unreadCount: action.unreadCount || 0,
                isLoading: false
            };
        case VOICEMAIL_MESSAGE_SELECTED:
            return {
                ...state,
                currentMessage: action.message || null,
                playbackUrl: action.message?.playbackUrl || null
            };
        case VOICEMAIL_MESSAGE_DELETED:
            return {
                ...state,
                messages: state.messages.filter(m => m.id !== action.messageId),
                totalCount: Math.max(0, state.totalCount - 1),
                isPlayerOpen: state.currentMessage?.id === action.messageId
                    ? false
                    : state.isPlayerOpen,
                currentMessage: state.currentMessage?.id === action.messageId
                    ? null
                    : state.currentMessage
            };
        case VOICEMAIL_MESSAGE_SEEN: {
            const messageId = action.messageId;
            return {
                ...state,
                messages: state.messages.map(m => m.id === messageId ? { ...m, seen: true } : m),
                unreadCount: Math.max(0, state.unreadCount - 1)
            };
        }
        case VOICEMAIL_UNREAD_COUNT_UPDATED:
            return { ...state, unreadCount: action.count || 0 };
        case VOICEMAIL_CAPTURE_REQUESTED:
            return { ...state, isCaptureRequested: true };
        case VOICEMAIL_RECORDING_STARTED:
            // REST confirmation and the room's WebSocket notification can
            // both report this same session. A late duplicate must not
            // reopen a finished take or undo a press while the recorder
            // is still joining.
            if (state.isRecordingReadyToSend
                && state.recordingSession?.messageId === action.session?.messageId) {
                return state;
            }
            return {
                ...state,
                isCaptureRequested: state.isRecording
                    && state.recordingSession?.messageId === action.session?.messageId
                    && state.recordingSession?.roomName === action.session?.roomName
                    && state.isCaptureRequested,
                isRecording: true,
                isRecordingReadyToSend: false,
                recordingSession: action.session || null,
                isPromptVisible: false,
                promptData: null
            };
        case VOICEMAIL_RECORDING_COMPLETE: {
            const session = state.recordingSession;
            if (!action.messageId || !session || session.messageId !== action.messageId) {
                return state;
            }
            return {
                ...state,
                isCaptureRequested: false,
                isRecording: false,
                isRecordingReadyToSend: true,
                recordingSession: {
                    ...session,
                    durationSeconds: action.durationSeconds,
                    kind: action.kind || session.kind
                }
            };
        }
        case VOICEMAIL_RECORDING_CANCELLED:
            return {
                ...state,
                isCaptureRequested: false,
                isRecording: false,
                isRecordingReadyToSend: false,
                recordingSession: null
            };
        case VOICEMAIL_RECORDING_SENT:
            return {
                ...state,
                isCaptureRequested: false,
                isRecording: false,
                isRecordingReadyToSend: false,
                recordingSession: null
            };
        case VOICEMAIL_PROMPT_SHOWN:
            return {
                ...state,
                isPromptVisible: true,
                promptData: action.data || null
            };
        case VOICEMAIL_PROMPT_DISMISSED:
            return {
                ...state,
                isPromptVisible: false,
                promptData: null
            };
        case VOICEMAIL_PLAYER_OPENED:
            return { ...state, isPlayerOpen: true };
        case VOICEMAIL_PLAYER_CLOSED:
            return {
                ...state,
                isPlayerOpen: false,
                currentMessage: null,
                playbackUrl: null
            };
        case VOICEMAIL_ERROR:
            return {
                ...state,
                error: action.error || 'Unknown error',
                isCaptureRequested: false,
                isLoading: false,
                isRecording: false,
                isRecordingReadyToSend: false,
                recordingSession: null
            };
        case VOICEMAIL_CLEAR_ERROR:
            return { ...state, error: null };
        case 'VOICEMAIL_OPEN_INBOX':
            return { ...state, inboxOpen: true };
        case 'VOICEMAIL_CLOSE_INBOX':
            return { ...state, inboxOpen: false };
        default:
            return state;
    }
});
